import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/api';  // Add this line
const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('userData');
        return savedUser && savedUser !== "undefined" ? JSON.parse(savedUser) : null;
    });
    const [loading, setLoading] = useState(false);
    const [initialCheckDone, setInitialCheckDone] = useState(false);
    
    const [requiresSkills, setRequiresSkills] = useState(false);

    useEffect(() => {
        const checkUserSession = async () => {
            try {
                if (localStorage.getItem('userData')) {
                    const response = await authService.getCurrentUser();
                    if (response.data.success) {
                        setUser(response.data.user);
                        localStorage.setItem('userData', JSON.stringify(response.data.user));
                        setRequiresSkills(response.data.requiresSkills || false);
                    }
                }
            } catch (error) {
                console.error('Session check error:', error);
                localStorage.removeItem('userData');
                setUser(null);
                setRequiresSkills(false);
            } finally {
                setLoading(false);
                setInitialCheckDone(true);
            }
        };

        checkUserSession();
    }, []);

    const login = async (userData) => {
        setLoading(true);
        try {
            setUser(userData);
            localStorage.setItem('userData', JSON.stringify(userData));
            setRequiresSkills(userData.requiresSkills || false);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            const response = await authService.logout();
            if (response.data.success) {
                setUser(null);
                localStorage.removeItem('userData');
                localStorage.removeItem('isAuthenticated');
                setRequiresSkills(false);
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setLoading(false);
        }
    };

    const switchRole = async (roleId) => {
        try {
            const response = await authService.switchRole(roleId);

            if (response.data.status === 'success') {
                const updatedUser = {
                    ...user,
                    role_id: response.data.role_id,
                    current_role_id: response.data.current_role_id,
                    role_name: response.data.role_name,
                    permissions: response.data.permissions,
                    // The roles as the server has them now, so a role given since sign-in counts (K21.2 pt 41).
                    roles: response.data.roles || user?.roles
                };
                setUser(updatedUser);
                localStorage.setItem('userData', JSON.stringify(updatedUser));
                setRequiresSkills(!updatedUser.hasSkills);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Role switch error:', error);
            return false;
        }
    };

    const updateUserProfile = (profileData) => {
        if (!user) return;
        const updatedUser = { ...user, ...profileData };
        setUser(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));

        if (profileData.hasSkills !== undefined || profileData.requiresSkills !== undefined) {
            setRequiresSkills(profileData.requiresSkills || false);
        }

    };

    const handleSkillsCompleted = () => {
        if (!user) return;
        
        const updatedUser = {
            ...user,
            hasSkills: true,
            requiresSkills: false
        };
        
        setUser(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        setRequiresSkills(false);
    };

    const checkSkillsRequirement = () => {
        if (!user) return false;        
        return !user.hasSkills;
    };


    const value = {
        user,
        setUser,
        loading,
        initialCheckDone,
        login,
        logout,
        switchRole,
        updateUserProfile,
        handleSkillsCompleted,
        checkSkillsRequirement,
        setRequiresSkills
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};
export const useUser = () => useContext(UserContext);